import { useEffect, useState } from "react";
import { api, apiError } from "./api";
import { Card, PageHead, Btn, Input, Field } from "./widgets";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "About" },
  { key: "services", label: "Services Overview" },
  { key: "services/tax-accounting", label: "Tax & Accounting" },
  { key: "services/asset-protection", label: "Asset Protection" },
  { key: "services/estate-planning", label: "Estate Planning" },
  { key: "services/strategic-investments", label: "Strategic Investments" },
  { key: "services/immigration", label: "Immigration Support" },
  { key: "services/business-consulting", label: "Business Consulting" },
  { key: "resources", label: "Resources" },
  { key: "blog", label: "Blog" },
  { key: "faq", label: "FAQ" },
  { key: "contact", label: "Contact" },
  { key: "privacy-policy", label: "Privacy Policy" },
  { key: "terms", label: "Terms of Use" },
  { key: "accessibility", label: "Accessibility" },
  { key: "editorial-policy", label: "Editorial Policy" },
  { key: "disclosures", label: "Disclosures" },
  { key: "author/escarling-martinez", label: "Author — Escarling Martínez" },
];

const HealthItem = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-[0.65rem] ${ok ? "text-emerald-700" : "text-[#171717]/40"}`}>
    {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {label}
  </span>
);

const SeoManager = () => {
  const [doc, setDoc] = useState(null);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/content/seo").then((r) => {
      setDoc(r.data);
      setData(r.data.draft ?? r.data.published ?? {});
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (key, field, value) =>
    setData({ ...data, [key]: { ...(data[key] || {}), [field]: value } });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content/seo/draft", { data });
      await api.post("/admin/content/seo/publish");
      toast.success("SEO settings published.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!doc) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  return (
    <div data-testid="seo-manager">
      <PageHead
        title="SEO Manager"
        sub="Per-page titles, descriptions and indexing. Empty fields keep the handcrafted defaults. These influence — never guarantee — rankings."
        actions={<Btn primary onClick={save} disabled={busy} data-testid="seo-save">Save & Publish</Btn>}
      />
      <div className="space-y-4">
        {PAGES.map((p) => {
          const v = data[p.key] || {};
          return (
            <Card key={p.key} data-testid={`seo-${p.key}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-44">
                  <p className="text-sm text-[#171717]">{p.label}</p>
                  <p className="mt-0.5 font-mono text-[0.62rem] text-[#171717]/40">/{p.key === "home" ? "" : p.key}</p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <HealthItem ok={Boolean(v.title)} label="Title set" />
                    <HealthItem ok={Boolean(v.description)} label="Description set" />
                    <HealthItem ok={v.description ? v.description.length >= 50 && v.description.length <= 170 : false} label="Desc. 50–170 chars" />
                    <HealthItem ok={!v.noindex} label="Indexable" />
                  </div>
                </div>
                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <Field label="SEO Title"><Input value={v.title || ""} onChange={(e) => set(p.key, "title", e.target.value)} placeholder="Default from page" /></Field>
                  <Field label="Meta Description"><Input value={v.description || ""} onChange={(e) => set(p.key, "description", e.target.value)} placeholder="Default from page" /></Field>
                </div>
                <label className="flex items-center gap-2 text-xs text-[#171717]/60">
                  <input
                    type="checkbox"
                    checked={Boolean(v.noindex)}
                    onChange={(e) => set(p.key, "noindex", e.target.checked)}
                    className="h-4 w-4 accent-[#C6A46A]"
                  />
                  Noindex
                </label>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SeoManager;
